import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiCalendar,
  FiClock,
  FiUser,
  FiInfo,
  FiCreditCard,
  FiMail,
  FiPhone,
} from "react-icons/fi";
import { IoCalendarOutline, IoClose } from "react-icons/io5";
import Swal from "sweetalert2";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import 'react-phone-number-input/style.css'
import PhoneInput from 'react-phone-number-input'
import HtmlContent from "../common/HtmlContent";
import '../../styles/html-content.css';

const TourForm = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(1);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingPrice, setLoadingPrice] = useState(false);
  const [loadingTours, setLoadingTours] = useState(true);
  const [tours, setTours] = useState([]);
  const [errors, setErrors] = useState({});
  const [activeTourId, setActiveTourId] = useState(null);
  const [activeTourDetails, setActiveTourDetails] = useState(null);
  const [showTourDetails, setShowTourDetails] = useState(false);
  const [priceEstimate, setPriceEstimate] = useState(null);
  const [formData, setFormData] = useState({
    tourId: "",
    tourName: "",
    tourDate: new Date(),
    tourTime: { hours: "10", minutes: "00" },
    tourPersons: 2,
    tourPrice: 0,
    name: "",
    surname: "",
    email: "",
    phone: "",
    pickupLocation: "",
    otherPickupLocation: "",
    carType: "car",
    paymentMethod: "credit-card",
    additionalInfo: "",
    travelGuide: { available: false, time: "" },
    acceptedTerms: false,
  });

  // Fetch available tours
  useEffect(() => {
    const fetchTours = async () => {
      try {
        setLoadingTours(true);
        const response = await axios.get("http://localhost:5000/api/tours");
        // Filter out inactive tours
        const activeTours = response.data.filter(tour => tour.isActive !== false);
        setTours(activeTours);
      } catch (error) {
        console.error("Error fetching tours:", error);
        setTours([]);
      } finally {
        setLoadingTours(false);
      }
    };

    fetchTours();
  }, []);

    useEffect(() => {
      // Wait for DOM to be ready
        if (activeStep === 2) {
        const phoneInput = document.getElementsByClassName( "PhoneInputInput")[0];
        if (phoneInput) {
          phoneInput.className =
            "PhoneInputInput rounded-lg p-3 text-gray-800 placeholder-gray-100 focus:ring-2 focus:ring-blue-500 outline-none bg-white/70 placeholder-shown:bg-white/30 backdrop-blur-sm";
        }
      }
    }, [activeStep]);

  const locationOptions = [
    { value: "AthensAirport", label: "Athens Airport", icon: "✈️" },
    { value: "AthensCityHotels", label: "Athens City/Hotels", icon: "🏙️" },
    { value: "PiraeusPort", label: "Piraeus Port", icon: "🚢" },
    { value: "PiraeusCity", label: "Piraeus City", icon: "🏙️" },
    { value: "LavrioPort", label: "Lavrio Port", icon: "🚢" },
    { value: "LavrioCity", label: "Lavrio City", icon: "🏙️" },
    { value: "other", label: "Other Location", icon: "📍" },
  ];

  const carTypeOptions = [
    { value: "car", label: "Car up to 2 pax" },
    { value: "minivan", label: "Mini Van up to 7 pax" },
    { value: "van", label: "Van up to 12 pax" },
    { value: "minibus", label: "Mini Bus up to 20 pax" },
  ];

  // Calculate price estimate directly from tour data when tour, date, persons or vehicle type changes
  useEffect(() => {
    const calculatePriceEstimate = () => {
      if (!activeTourId || !formData.tourPersons) {
        setPriceEstimate(null);
        return;
      }

      try {
        setLoadingPrice(true);

        // Find the selected tour in the tours array
        const selectedTour = tours.find((tour) => tour._id === activeTourId);

        if (!selectedTour || !selectedTour.pricing) {
          setPriceEstimate(null);
          return;
        }

        // Determine the season based on current date
        const currentDate = new Date(formData.tourDate);
        const month = currentDate.getMonth() + 1; // JavaScript months are 0-based

        // Determine the season based on the date
        let season = "lowSeason"; // Default to low season
        const day = currentDate.getDate();

        // High Season: July 11 - August 25
        if ((month === 7 && day >= 11) || (month === 8 && day <= 25)) {
          season = "highSeason";
        }
        // Medium Season: June 26 - July 10, August 26 - September 15
        else if (
          (month === 6 && day >= 26) ||
          (month === 7 && day <= 10) ||
          (month === 8 && day >= 26) ||
          (month === 9 && day <= 15)
        ) {
          season = "mediumSeason";
        }
        // Low Season: April 1 - June 25, September 16 - October 30
        else if (
          (month >= 4 && month <= 6 && !(month === 6 && day > 25)) ||
          (month === 9 && day >= 16) ||
          (month === 10 && day <= 30)
        ) {
          season = "lowSeason";
        }        // Default to low season for any other dates (November through March)        // Get the price based on vehicle type and season
        const vehicleType = formData.carType || "car";
        const unitPrice = selectedTour.pricing[vehicleType][season];

        // Calculate total price based on pricing type
        let totalPrice = 0;
        if (selectedTour.pricingType === "fixed") {
          // Fixed pricing - price is the same regardless of number of persons
          totalPrice = unitPrice;
        } else {
          // Per person pricing - multiply by number of participants
          totalPrice = unitPrice * formData.tourPersons;
        }

        // Set the price estimate object
        setPriceEstimate({
          unitPrice,
          totalPrice,
          season,
          pricingType: selectedTour.pricingType || "perPerson",
        });
      } catch (error) {
        console.error("Error calculating price estimate:", error);
        setPriceEstimate(null);
      } finally {
        setLoadingPrice(false);
      }
    };

    calculatePriceEstimate();
  }, [
    activeTourId,
    formData.tourDate,
    formData.tourPersons,
    formData.carType,
    tours,
  ]);

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === "checkbox") {
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }

    // Clear error when field is changed
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // Handle date change
  const handleDateChange = (date) => {
    setFormData((prev) => ({
      ...prev,
      tourDate: date,
    }));
  };

  // Handle tour selection
  const handleTourSelect = (tourId) => {
    if (activeTourId === tourId) {
      // Clicking on the selected tour unselects it
      setActiveTourId(null);
      setShowTourDetails(false);
      setFormData((prev) => ({
        ...prev,
        tourId: "",
        tourName: "",
      }));
    } else {
      // Select a new tour
      const selectedTour = tours.find((tour) => tour._id === tourId);
      setActiveTourId(tourId);
      setActiveTourDetails(selectedTour);
      setShowTourDetails(true);
      setFormData((prev) => ({
        ...prev,
        tourId: tourId,
        tourName: selectedTour.name || "",
      }));
    }
  };

  // Toggle tour details
  const toggleTourDetails = (e, tourId) => {
    e.stopPropagation();

    if (activeTourId === tourId) {
      setShowTourDetails(!showTourDetails);
    } else {
      const selectedTour = tours.find((tour) => tour._id === tourId);
      setActiveTourId(tourId);
      setActiveTourDetails(selectedTour);
      setShowTourDetails(true);
      setFormData((prev) => ({
        ...prev,
        tourId: tourId,
        tourName: selectedTour.name || "",
      }));
    }
  };

  // Determine the base price for a tour based on vehicle type
  const getBasePrice = (tour, vehicleType = "car") => {
    if (!tour || !tour.pricing || !tour.pricing[vehicleType]) {
      return 0;
    }
    return tour.pricing[vehicleType].mediumSeason;
  };

  // Prevent event bubbling for child elements
  const handleFormElementClick = (e) => {
    e.stopPropagation();
  };

     // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const TOURS_PER_PAGE = 3;
  
    // Calculate paginated tours
    const totalPages = Math.ceil(tours.length / TOURS_PER_PAGE);
    const paginatedTours = tours.slice(
      (currentPage - 1) * TOURS_PER_PAGE,
      currentPage * TOURS_PER_PAGE
    );
  
    // Reset to first page if tours change and current page is out of range
    useEffect(() => {
      if (currentPage > totalPages) {
        setCurrentPage(1);
      }
    }, [tours, totalPages]);

  // Navigate between steps
  const nextStep = () => {
    // Validate fields before proceeding
    const newErrors = {};

    if (activeStep === 1) {
      if (!activeTourId) {
        newErrors.tourId = "Please select a tour";
      }
      if (!formData.tourPersons) {
        newErrors.tourPersons = "Please enter number of people";
      }
    } else if (activeStep === 2) {
      if (!formData.name) newErrors.name = "Please enter your first name";
      if (!formData.surname) newErrors.surname = "Please enter your last name";
      if (!formData.email) {
        newErrors.email = "Please enter your email";
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = "Please enter a valid email address";
      }
      if (!formData.phone) {
        newErrors.phone = "Please enter your phone number";
      }
      // Check if phone contains area code, e.g. +30, +1, etc.
      if (formData.phone && !/^\+\d{1,3}\d{9,}$/.test(formData.phone)) {
        newErrors.phone = "Please enter a valid phone number with area code";
      }
      if (!formData.pickupLocation) {
        newErrors.pickupLocation = "Please select a pickup location";
      }
      if (
        formData.pickupLocation === "other" &&
        !formData.otherPickupLocation
      ) {
        newErrors.otherPickupLocation = "Please enter the pickup location";
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsAnimating(true);
    setTimeout(() => {
      setActiveStep((prev) => prev + 1);
      setIsAnimating(false);

      // Push a new history state when advancing to a new step
      window.history.pushState(null, document.title, window.location.pathname);
    }, 300);
  };

  const prevStep = () => {
    setIsAnimating(true);
    setTimeout(() => {
      setActiveStep((prev) => prev - 1);
      setIsAnimating(false);
    }, 300);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.acceptedTerms) {
      setErrors((prev) => ({
        ...prev,
        acceptedTerms: "You must accept the terms before submitting",
      }));
      return;
    }

    setIsSubmitting(true);

    // generate a valid object ID format for transfer:   const isValidObjectId = (id) => /^[0-9a-fA-F]{24}$/.test(id);
    const isValidObjectId = (id) => /^[0-9a-fA-F]{24}$/.test(id);

    try {
      // Format the tour time
      const tourDateTime = new Date(formData.tourDate);
      tourDateTime.setHours(
        parseInt(formData.tourTime.hours),
        parseInt(formData.tourTime.minutes)
      );

      // Prepare data for submission
      const tourBookingData = {
        transfer: isValidObjectId(formData.tourId) ? formData.tourId : null,
        tour: formData.tourId,
        name: `${formData.name} ${formData.surname}`,
        email: formData.email,
        phone: formData.phone,
        vehicleType: formData.carType,
        tourDate: tourDateTime,
        tourTime: formData.tourTime,
        numberOfPersons: formData.tourPersons,
        pickupLocation:
          formData.pickupLocation === "other"
            ? formData.otherPickupLocation
            : locationOptions.find(
                (opt) => opt.value === formData.pickupLocation
              )?.label,
        notes: formData.additionalInfo,
        paymentMethod: formData.paymentMethod,
        travelGuide: {
          available: !!formData.travelGuide.available,
          time: formData.travelGuide.available ? formData.travelGuide.time : "",
        },
      };

      // Submit tour booking
      await axios.post(
        "http://localhost:5000/api/tour-bookings",
        tourBookingData
      );

      Swal.fire({
        title: "Success!",
        text: "Your tour booking has been submitted successfully!",
        icon: "success",
        confirmButtonColor: "#1e3a8a",
      }).then(() => {
        // Reset form
        setFormData({
          tourId: "",
          tourName: "",
          tourDate: new Date(),
          tourTime: { hours: "10", minutes: "00" },
          tourPersons: 2,
          tourPrice: 0,
          name: "",
          surname: "",
          email: "",
          phone: "",
          pickupLocation: "",
          otherPickupLocation: "",
          carType: "car",
          paymentMethod: "credit-card",
          additionalInfo: "",
          travelGuide: { available: false, time: "" },
          acceptedTerms: false,
        });
        setActiveStep(1);
        setActiveTourId(null);
        setShowTourDetails(false);
      });
    } catch (error) {
      console.error("Error submitting tour booking:", error);
      Swal.fire({
        title: "Error!",
        text: "Failed to submit your booking. Please try again.",
        icon: "error",
        confirmButtonColor: "#1e3a8a",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Add history handling for mobile back gesture
  useEffect(() => {
    // Function to handle browser's back button or gesture
    const handleBackNavigation = (event) => {
      // Only intercept back navigation if we're past the first step
      if (activeStep > 1) {
        // Prevent the default back behavior
        event.preventDefault();

        // Go back one step in the form instead
        prevStep();

        // Push a new entry to maintain history stack
        window.history.pushState(
          null,
          document.title,
          window.location.pathname
        );

        return;
      }
      // If on first step, allow normal back functionality
    };

    // Add a history entry when component mounts
    window.history.pushState(null, document.title, window.location.pathname);

    // Listen for popstate events (back navigation)
    window.addEventListener("popstate", handleBackNavigation);

    // Cleanup listener on component unmount
    return () => {
      window.removeEventListener("popstate", handleBackNavigation);
    };
  }, [activeStep]);

  const backgroundImage = require("../../assets/images/athens.jpg");

  const hourOptions = [
    "09",
    "10",
    "11",
    "12",
    "13",
    "14",
    "15",
    "16",
    "17",
    "18",
    "19",
  ];
  const minuteOptions = ["00", "15", "30", "45"];

  // Format season name for display
  const getSeasonName = (seasonKey) => {
    if (seasonKey === "lowSeason") return "Low Season";
    if (seasonKey === "highSeason") return "High Season";
    return "Medium Season";
  };

  return (
    <>
      <main
        className="flex-grow bg-cover bg-center bg-no-repeat py-12"
        id="tour-form"
      >
        <div className="container mx-auto py-8 px-4 relative z-10">
          <div className="bg-white/10 backdrop-blur-md rounded-xl shadow-2xl overflow-hidden border border-white/20 relative">
            {/* Progress bar */}
            <div className="h-1.5 bg-gray-200/30">
              <div
                className="h-full bg-gradient-to-r from-gray-700 to-black transition-all duration-300 ease-in-out"
                style={{ width: `${(activeStep / 3) * 100}%` }}
              ></div>
            </div>
            {/* Dark overlay for better contrast */}
            <div className="absolute inset-0 bg-black/30 backdrop-blur-sm -z-10 rounded-xl"></div>
            {/* Form Content */}
            <div className="p-6 md:p-10">
              <form onSubmit={handleSubmit} className="relative w-full">
                <div
                  className={`transition-opacity duration-300 ${
                  isAnimating ? "opacity-0" : "opacity-100"
                  }`}
                >
                  {/* Step 1: Tour Selection */}
                  {activeStep === 1 && (
                  <div className="space-y-10 animate-fadeIn">
                    <h2 className="text-2xl md:text-3xl font-bold text-gray-200 mb-6">
                    Book Your Athens Tour
                    </h2>

                    <div className="mb-6">
                      <h3 className="text-xl font-semibold text-gray-200 mb-4">
                        Available Tours
                      </h3>
                      {loadingTours ? (
                        <div className="flex items-center text-gray-200 space-x-2">
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Loading tours...</span>
                        </div>
                      ) : tours.length === 0 ? (
                        <div className="text-gray-200 py-4">
                        No tours are currently available.
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 gap-4 mb-8">
                        {paginatedTours.map((tour) => (
                          <div
                            key={tour._id}
                            className={`border rounded-lg overflow-hidden shadow-sm hover:shadow-md transition cursor-pointer
                              ${
                              activeTourId === tour._id
                                ? "border-red-500 border-2 bg-red-50/40"
                                : "border-white/40 bg-white/30"
                              }`}
                            onClick={() => handleTourSelect(tour._id)}
                          >
                            <div className="flex flex-col md:flex-row">
                              {tour.imageUrl && (
                              <div className="w-full md:w-1/3 h-48 md:h-auto">
                                <img
                                src={`${"http://localhost:5000"}${
                                  tour.imageUrl.startsWith("/")
                                  ? ""
                                  : "/"
                                }${tour.imageUrl}`}
                                alt={tour.name}
                                className="w-full h-full object-cover"
                                />
                              </div>
                              )}
                              <div className="p-4 flex-1">
                                <div className="flex w-full justify-between">
                                  <h4 className="text-lg font-semibold text-gray-200">
                                    {tour.name}
                                  </h4>
                                  
                                  {activeTourId === tour._id 
                                    ? <span className="text-red-600 font-semibold">SELECTED</span>
                                    : null
                                  }
                                </div>
                                <div className="flex items-center mt-2 text-gray-300">
                                  <span className="mr-4">
                                  Duration: {tour.duration} hours
                                  </span>

                                </div>
                                {/* Use HtmlContent for description */}
                                <div className="mt-2 text-sm text-gray-300 line-clamp-2">
                                  <HtmlContent html={tour.description} />
                                </div>
                                <div className="mt-3 flex justify-between items-center">
                                  <button
                                  type="button"
                                  onClick={(e) =>
                                    toggleTourDetails(e, tour._id)
                                  }
                                  className="text-blue-300 text-sm flex items-center"
                                  >
                                  <FiInfo className="mr-1" />
                                  {showTourDetails &&
                                  activeTourId === tour._id
                                    ? "Hide details"
                                    : "View details"}
                                  </button>
                                  <div className="text-blue-300 font-semibold">
                                  From €
                                  {tour.pricing?.car?.lowSeason ||
                                    getBasePrice(tour)}
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Tour details section */}
                            {showTourDetails &&
                              activeTourId === tour._id && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                transition={{ duration: 0.3 }}
                                className="border-t border-white/30 bg-white/30 p-4"
                                onClick={handleFormElementClick}
                              >
                                <h5 className="font-semibold mb-2 text-gray-800">
                                Tour Details
                                </h5>
                                <div className="mb-3 text-gray-700">
                                  <HtmlContent html={tour.description} />
                                </div>

                                {tour.highlights &&
                                tour.highlights.length > 0 && (
                                  <div className="mb-3">
                                    <h6 className="font-medium mb-1 text-gray-800">
                                      Highlights:
                                    </h6>
                                    <div className="pl-5 text-gray-700">
                                      {tour.highlights.map((highlight, idx) => (
                                        <div key={idx}>
                                          <HtmlContent html={highlight} />
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {tour.locations &&
                                tour.locations.length > 0 && (
                                  <div className="mb-3">
                                    <h6 className="font-medium mb-1 text-gray-800">
                                      Locations:
                                    </h6>
                                    <div className="pl-5 text-gray-700">
                                      {tour.locations.map((location, idx) => (
                                        <div key={idx}>
                                          <HtmlContent html={location} />
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                <h5 className="font-semibold mt-4 mb-2 text-gray-800">
                                Book This Tour
                                </h5>
                                                                {/* Vehicle Selection */}
                                <div className="relative items-center content-center text-center">
                                <h3 className="text-lg font-semibold text-gray-700 mb-4">
                                  Choose Vehicle Type
                                </h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8 w-full">
                                  {carTypeOptions.map((option) => (
                                  <motion.div
                                    key={option.value}
                                    onClick={() =>
                                    setFormData((prev) => ({
                                      ...prev,
                                      carType: option.value,
                                    }))
                                    }
                                    className={`relative cursor-pointer rounded-xl shadow-md overflow-hidden transition-all duration-300 border-2 ${
                                    formData.carType === option.value
                                      ? "border-black ring-2 ring-gray-400"
                                      : "border-white/30 hover:border-gray-400"
                                    }`}
                                    whileHover={{
                                    y: -5,
                                    boxShadow: "0 10px 25px rgba(0, 0, 0, 0.1)",
                                    }}
                                    whileTap={{ scale: 0.98 }}
                                  >
                                    <div className="p-5 text-center h-full bg-gradient-to-b from-white/30 to-gray-200/90 backdrop-blur-sm">
                                    <div className="w-full h-28 flex items-center justify-center mb-3 overflow-hidden">
                                      <img
                                      src={(() => {
                                        switch (option.value) {
                                        case "car":
                                          return require("../../assets/images/emerald_green.webp");
                                        case "minivan":
                                          return require("../../assets/images/mercedes-vito_tourer_1.png");
                                        case "van":
                                          return require("../../assets/images/V-Class-Marco-Polo-1.png");
                                        case "minibus":
                                          return require("../../assets/images/fleet-Mercedes-Benz-Sprinter-Passenger-Van.png");
                                        default:
                                          return require("../../assets/images/mercedes-vito_tourer_1.png");
                                        }
                                      })()}
                                      alt={option.label}
                                      className="object-contain max-h-full max-w-full"
                                      />
                                    </div>
                                        <h4 className="font-semibold text-lg text-gray-800">
                                          {option.label.split('(')[0].trim()}
                                        </h4>
                                        <h2 className="text-gray-600 text-sm mt-1">
                                          {/* Extract max persons from the label */}
                                          {option.label.includes('(') ? option.label.split('(')[1].replace(')', '') : ''}
                                        </h2>
                                    </div>
                                  </motion.div>
                                  ))}
                                </div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 ">
                                <div className="w-full">
                                  <label className="block text-sm font-medium mb-1 text-gray-700">
                                  <FiCalendar className="inline mr-1" />{" "}
                                  Tour Date
                                  </label>
                                  <DatePicker
                                  selected={formData.tourDate}
                                  onChange={handleDateChange}
                                  dateFormat="dd/MM/yyyy"
                                  calendarClassName="w-full"
                                  wrapperClassName="w-full"
                                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                                  minDate={new Date()}
                                  />
                                </div>
                                {/* Tour Guide Yes/No, and for how many hours */}
                                <div>
                                  <label className="block text-sm font-medium mb-1 text-gray-700">
                                  <FiUser className="inline mr-1" /> Tour Guide
                                  </label>
                                  <select
                                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                                  value={formData.travelGuide.available ? "yes" : "no"}
                                  onChange={(e) =>
                                  setFormData((prev) => ({
                                  ...prev,
                                  travelGuide: {
                                    ...prev.travelGuide,
                                    available: e.target.value === "yes",
                                    time: e.target.value === "yes" ? prev.travelGuide.time : "",
                                  },
                                  }))
                                  }
                                  >
                                  <option value="no">No</option>
                                  <option value="yes">Yes</option>
                                  </select>
                                  {formData.travelGuide.available && (
                                  <input
                                  type="text"
                                  className="mt-2 w-full px-3 py-2 border border-gray-300 rounded text-sm"
                                  placeholder="Guide for how many hours? (e.g. 2h, 3h)"
                                  value={formData.travelGuide.time}
                                  onChange={(e) =>
                                  setFormData((prev) => ({
                                    ...prev,
                                    travelGuide: {
                                    ...prev.travelGuide,
                                    time: e.target.value,
                                    },
                                  }))
                                  }
                                  required={formData.travelGuide.available}
                                  />
                                  )}
                                </div>

                                <div>
                                  <label className="block text-sm font-medium mb-1 text-gray-700">
                                  <FiUser className="inline mr-1" />{" "}
                                  Number of Persons
                                  </label>
                                  <select
                                  className={`w-full px-3 py-2 border ${errors.tourPersons ? "border-red-500" : "border-gray-300"} rounded text-sm`}
                                  value={formData.tourPersons}
                                  onChange={(e) => {
                                    const newPersons = Number(e.target.value);
                                    const selectedTour = tours.find(tour => tour._id === activeTourId);
                                    const tourMaxPassengers = selectedTour?.maxPassengers;
                                    
                                    // Clear any previous errors
                                    if (errors.tourPersons) {
                                      setErrors(prev => ({...prev, tourPersons: ""}));
                                    }
                                    
                                    setFormData(prev => ({
                                      ...prev,
                                      tourPersons: newPersons
                                    }));
                                  }}
                                  >
                                  {(() => {
                                    // Determine min and max persons based on vehicle type
                                    let minPersons, maxPersons;
                                    
                                    switch(formData.carType) {
                                      case 'car':
                                        minPersons = 1;
                                        maxPersons = 3;
                                        break;
                                      case 'minivan':
                                        minPersons = 4;
                                        maxPersons = 7;
                                        break;
                                      case 'van':
                                        minPersons = 8;
                                        maxPersons = 12;
                                        break;
                                      case 'minibus':
                                        minPersons = 13;
                                        maxPersons = 20;
                                        break;
                                      default:
                                        minPersons = 1;
                                        maxPersons = 3;
                                    }
                                    
                                    // Check if the selected tour has a maximum passengers limit
                                    const selectedTour = tours.find(tour => tour._id === activeTourId);
                                    if (selectedTour?.maxPassengers && selectedTour.maxPassengers < maxPersons) {
                                      maxPersons = selectedTour.maxPassengers;
                                    }
                                    
                                    // If current selection is out of new range, adjust it
                                    if (formData.tourPersons < minPersons || formData.tourPersons > maxPersons) {
                                      // Automatically adjust to the closest valid value
                                      setTimeout(() => {
                                        setFormData(prev => ({
                                          ...prev,
                                          tourPersons: formData.tourPersons < minPersons ? minPersons : maxPersons
                                        }));
                                      }, 0);
                                    }
                                    
                                    // Generate options from min to max
                                    const options = [];
                                    for (let i = minPersons; i <= maxPersons; i++) {
                                      options.push(
                                        <option key={i} value={i}>
                                          {i}
                                        </option>
                                      );
                                    }
                                    
                                    return options;
                                  })()}
                                  </select>
                                  {errors.tourPersons && (
                                    <p className="text-red-800 text-sm mt-1">{errors.tourPersons}</p>
                                  )}
                                </div>
                                </div>

                                {loadingPrice ? (
                                <div className="text-center py-2 mt-4">
                                  Calculating price...
                                </div>                                ) : priceEstimate ? (
                                <div className="bg-blue-50 p-3 rounded mt-4">
                                  <div className="mb-1 text-gray-700">
                                  <span className="font-medium">
                                  Pricing (
                                  {getSeasonName(
                                  priceEstimate.season
                                  )}
                                  ):
                                  </span>{" "}
                                  €{priceEstimate.unitPrice} 
                                  {priceEstimate.pricingType === "fixed" 
                                  ? " fixed price" 
                                  : " per person"}
                                  </div>
                                  <div className="text-lg font-semibold text-blue-700">
                                  Total: €{priceEstimate.totalPrice}
                                  </div>
                                </div>
                                ) : (
                                <div className="text-gray-500 mt-4">
                                  Select dates and number of persons to
                                  see pricing
                                </div>
                                )}
                                </motion.div>
                                )}
                                </div>
                                ))}
                                </div>
                                )}
                                {/* Pagination Controls */}
                  {totalPages > 1 && (
                  <div className="flex justify-center items-center space-x-2 mt-4">
                    <button
                    type="button"
                    className="px-3 py-1 rounded bg-gray-200 text-gray-700 font-medium disabled:opacity-50"
                    onClick={(e) => { e.stopPropagation(); setCurrentPage((p) => Math.max(1, p - 1)); }}
                    disabled={currentPage === 1}
                    >
                    Previous
                    </button>
                    <span className="text-gray-400">
                    Page {currentPage} of {totalPages}
                    </span>
                    <button
                    type="button"
                    className="px-3 py-1 rounded bg-gray-200 text-gray-700 font-medium disabled:opacity-50"
                    onClick={(e) => { e.stopPropagation(); setCurrentPage((p) => Math.min(totalPages, p + 1)); }}
                    disabled={currentPage === totalPages}
                    >
                    Next
                    </button>
                  </div>
                  )}
                    </div>

                    {errors.tourId && (
                    <p className="text-red-500 mt-2">{errors.tourId}</p>
                    )}
                  </div>
                  )}

                  {/* Step 2: Contact Information */}
                  {activeStep === 2 && (
                    <div className="space-y-8 animate-fadeIn">
                      <h2 className="text-2xl md:text-3xl font-bold text-gray-200 mb-6">
                        Your Information
                      </h2>

                      <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <label className="font-medium text-gray-200">
                            Full Name
                          </label>
                          <div className="flex flex-col md:flex-row gap-4">
                            <div className="flex-1">
                              <input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                placeholder="First name"
                                className={`w-full border ${
                                  errors.name
                                    ? "border-red-500"
                                    : "border-white/30"
                                } rounded-lg p-3 text-gray-800 placeholder-gray-100 focus:ring-2 focus:ring-blue-500 outline-none bg-white/70 placeholder-shown:bg-white/30 backdrop-blur-sm`}
                              />
                              {errors.name && (
                                <p className="text-red-800 text-sm mt-1">
                                  {errors.name}
                                </p>
                              )}
                            </div>
                            <div className="flex-1">
                              <input
                                type="text"
                                name="surname"
                                value={formData.surname}
                                onChange={handleChange}
                                placeholder="Last name"
                                className={`w-full border ${
                                  errors.surname
                                    ? "border-red-500"
                                    : "border-white/30"
                                } rounded-lg p-3 text-gray-800 placeholder-gray-100 focus:ring-2 focus:ring-blue-500 outline-none bg-white/70 placeholder-shown:bg-white/30 backdrop-blur-sm`}
                              />
                              {errors.surname && (
                                <p className="text-red-800 text-sm mt-1">
                                  {errors.surname}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="space-y-2">
                          <label className="font-medium text-gray-200 flex items-center">
                            <FiMail className="mr-2" /> Email Address
                          </label>
                          <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="Email address"
                            className={`w-full border ${
                              errors.email
                                ? "border-red-500"
                                : "border-white/30"
                            } rounded-lg p-3 text-gray-800 placeholder-gray-100 focus:ring-2 focus:ring-blue-500 outline-none bg-white/70 placeholder-shown:bg-white/30 backdrop-blur-sm`}
                          />
                          {errors.email && (
                            <p className="text-red-800 text-sm mt-1">
                              {errors.email}
                            </p>
                          )}
                        </div>

                        <div className="space-y-2">
                          <label className="font-medium text-gray-200 flex items-center">
                            <FiPhone className="mr-2" /> Phone Number
                          </label>
                                <PhoneInput
                                  international
                                  defaultCountry="GR"
                                  value={formData.phone}
                                  onChange={(value) => {
                                  setFormData((prev) => ({
                                    ...prev,
                                    phone: value,
                                  }));
                                  handleChange({
                                    target: {
                                    name: "phone",
                                    value: value,
                                    },
                                  });
                                  }}
                                  placeholder="Phone number with country code"
                                  autoComplete="on"
                                />
                          {errors.phone && (
                            <p className="text-red-800 text-sm mt-1">
                              {errors.phone}
                            </p>
                          )}
                        </div>

                        <div className="space-y-2">
                          <label className="font-medium text-gray-200 flex items-center ">
                            Pickup Location
                          </label>
                          <select
                            name="pickupLocation"
                            value={formData.pickupLocation}
                            onChange={handleChange}
                            className={`w-full border ${
                              errors.pickupLocation
                                ? "border-red-500"
                                : "border-white/30"
                            } rounded-lg p-3 text-gray-800 focus:ring-2 focus:ring-blue-500 outline-none appearance-none bg-white/70 backdrop-blur-sm`}
                          >
                            <option value="">Select pickup location</option>
                            {locationOptions.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.icon} {option.label}
                              </option>
                            ))}
                          </select>

                          {formData.pickupLocation === "other" && (
                            <input
                              type="text"
                              name="otherPickupLocation"
                              value={formData.otherPickupLocation}
                              onChange={handleChange}
                              placeholder="Enter location details"
                              className={`w-full mt-2 border ${
                                errors.otherPickupLocation
                                  ? "border-red-500"
                                  : "border-gray-300"
                              } rounded-lg p-3 text-gray-800 placeholder-gray-300 focus:ring-2 focus:ring-blue-500 outline-none bg-white/70 backdrop-blur-sm placeholder-shown:bg-white/30`}
                            />
                          )}

                          {errors.pickupLocation && (
                            <p className="text-red-800 text-sm mt-1">
                              {errors.pickupLocation}
                            </p>
                          )}
                          {errors.otherPickupLocation && (
                            <p className="text-red-800 text-sm mt-1">
                              {errors.otherPickupLocation}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="w-full">
                        <label className="font-medium text-gray-200 flex items-center mb-2">
                          <FiCreditCard className="mr-2" /> Payment Method
                        </label>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {["Credit Card", "Cash"].map((method) => (
                            <label
                              key={method}
                              className="relative cursor-pointer"
                            >
                              <input
                                type="radio"
                                name="paymentMethod"
                                value={method.toLowerCase().replace(" ", "-")}
                                checked={formData.paymentMethod === method.toLowerCase().replace(" ", "-")}
                                onChange={handleChange}
                                className="sr-only"
                              />
                              <div
                                className={`flex items-center justify-center p-3 rounded-lg border-2 transition-all ${
                                  formData.paymentMethod === method.toLowerCase().replace(" ", "-")
                                    ? "border-gray-800 bg-gray-200"
                                    : "border-gray-200 hover:border-gray-300"
                                }`}
                              >
                                <FiCreditCard
                                  className={`mr-2 ${
                                    formData.paymentMethod === method.toLowerCase().replace(" ", "-")
                                      ? "text-gray-800"
                                      : "text-gray-400"
                                  }`}
                                />
                                <span
                                  className={formData.paymentMethod === method.toLowerCase().replace(" ", "-")
                                      ? "font-medium text-gray-800"
                                      : "text-gray-200"}
                                >
                                  {method}
                                </span>
                              </div>
                            </label>
                          ))}
                        </div>
                      </div>

                      <div className="w-full">
                        <label className="font-medium text-gray-200 flex items-center mb-2">
                          <FiInfo className="mr-2" /> Additional Information
                        </label>
                        <textarea
                          name="additionalInfo"
                          value={formData.additionalInfo}
                          onChange={handleChange}
                          placeholder="Any special requests or requirements"
                          className="w-full border border-white/30 rounded-lg p-3 h-32 text-gray-800 placeholder-gray-100 focus:ring-2 focus:ring-blue-500 outline-none bg-white/70 backdrop-blur-sm placeholder-shown:bg-white/30"
                        ></textarea>
                      </div>
                    </div>
                  )}

                  {/* Step 3: Confirmation */}
                  {activeStep === 3 && (
                    <div className="space-y-8 animate-fadeIn">
                      <h2 className="text-2xl md:text-3xl font-bold text-gray-200 mb-6">
                        Confirm Your Booking
                      </h2>

                      <div className="w-full space-y-6">
                        {/* Tour Summary Card */}
                        <div className="bg-white/30 backdrop-blur-md p-6 rounded-xl border border-white/20">
                          <h3 className="text-xl font-semibold text-gray-200 mb-4">
                            Tour Summary
                          </h3>
                          <div className="grid gap-6">
                            {activeTourDetails && (
                              <div className="bg-white/30 backdrop-blur-sm p-4 rounded-lg shadow-sm border border-white/30">
                                <div className="flex items-start">
                                  <div className="flex-grow">
                                    <h4 className="font-medium text-gray-800 text-lg">
                                      {activeTourDetails.name}
                                    </h4>
                                    <div className="text-sm text-gray-600 mt-1 line-clamp-3">
                                      <HtmlContent html={activeTourDetails.description} />
                                    </div>
                                    <div className="mt-3 flex flex-wrap gap-2 text-sm">
                                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-blue-100 text-blue-800">
                                        <FiClock className="mr-1" />{" "}
                                        {activeTourDetails.duration} hours
                                      </span>
                                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-green-100 text-green-800">
                                        <FiCalendar className="mr-1" />{" "}
                                        {new Date(
                                          formData.tourDate
                                        ).toLocaleDateString()}
                                      </span>
                                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-yellow-100 text-yellow-800">
                                        <FiUser className="mr-1" />{" "}
                                        {formData.tourPersons}{" "}
                                        {formData.tourPersons === 1
                                          ? "person"
                                          : "people"}
                                      </span>
                                      {/* tour guide */}
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-orange-100 text-orange-800">
                                        <FiUser className="mr-1" />{" "}
                                        {formData.travelGuide.available
                                          ? "Tour Guide for " +
                                            formData.travelGuide.time + "*"
                                          : "No Tour Guide"}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Price Summary */}
                            {priceEstimate && (
                              <div className="bg-white/30 backdrop-blur-sm p-4 rounded-lg shadow-sm border border-white/30">
                                <h4 className="font-medium text-gray-800 mb-3">
                                  Price Details
                                </h4>                                <div className="flex justify-between items-center border-b border-gray-200 pb-2">
                                  <span className="text-gray-600">
                                    {priceEstimate.pricingType === "fixed" ? "Fixed price" : "Price per person"} (
                                    {getSeasonName(priceEstimate.season)}):
                                  </span>
                                  <span className="font-medium">
                                    €{priceEstimate.unitPrice}
                                  </span>
                                </div>                                <div className="flex justify-between items-center border-b border-gray-200 py-2">
                                  <span className="text-gray-600">
                                    Number of persons:
                                    {priceEstimate.pricingType === "fixed" && <span className="text-xs text-gray-500 ml-1">(Fixed price)</span>}
                                  </span>
                                  <span className="font-medium">
                                    {formData.tourPersons}
                                  </span>
                                </div>
                                {/* if tour guide is available, then show that the price does not include the tour guide. Further communication will be needed. */}
                                {formData.travelGuide.available && (
                                  <div className="flex justify-between items-center border-b border-gray-200 py-2">
                                    <span className="text-gray-600">
                                      Tour Guide:
                                    </span>
                                    <div className="flex flex-col md:items-end">
                                    <span className="font-medium">
                                      {formData.travelGuide.time}*
                                    </span>
                                      <span className="text-xs text-gray-500">
                                        *(Price not included)
                                      </span>
                                    </div>
                                  </div>
                                )}
                                <div className="flex justify-between items-center pt-2 text-lg font-semibold">
                                  <span>Total:</span>
                                  <span className="text-blue-700">
                                    €{priceEstimate.totalPrice}
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Customer Details */}
                        <div className="bg-white/30 backdrop-blur-md p-6 rounded-xl border border-white/20">
                          <h4 className="font-bold text-gray-200 mb-4">
                            Customer Details
                          </h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <p className="text-sm text-gray-300 font-bold">
                                Full Name
                              </p>
                              <p className="font-medium text-gray-200">
                                {formData.name} {formData.surname}
                              </p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-300 font-bold">
                                Contact
                              </p>
                              <p className="font-medium text-gray-200">
                                {formData.phone}
                              </p>
                              <p className="text-sm text-gray-200">
                                {formData.email}
                              </p>
                            </div>
                            <div>
                              <p className="text-md text-gray-200 font-bold">
                                Pickup Location
                              </p>
                              <p className="font-medium text-gray-200">
                                {formData.pickupLocation === "other"
                                  ? formData.otherPickupLocation
                                  : locationOptions.find(
                                      (opt) =>
                                        opt.value === formData.pickupLocation
                                    )?.label}
                              </p>
                            </div>
                            <div>
                              <p className="text-md text-gray-200 font-bold">
                                Payment Method
                              </p>
                              <p className="font-medium text-gray-200">
                                {formData.paymentMethod
                                  .replace("-", " ")
                                  .replace(/\b\w/g, (char) =>
                                    char.toUpperCase()
                                  )}
                              </p>
                            </div>
                          </div>
                          {formData.additionalInfo && (
                            <div className="mt-4 border-t border-white/30 pt-4">
                              <p className="text-md text-gray-200 font-bold">
                                Additional Information
                              </p>
                              <p className="text-gray-200">
                                {formData.additionalInfo}
                              </p>
                            </div>
                          )}
                        </div>

                        <div className="w-full mt-8 bg-white/30 backdrop-blur-md p-6 rounded-lg border border-white/20">
                          <h4 className="font-medium text-gray-200 mb-4 flex items-center">
                            <FiInfo className="mr-2 text-indigo-200" />
                            Tour Information
                          </h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {[
                              "Professional English-speaking guide",
                              "Free cancellation up to 24 hours before",
                              "Children must be accompanied by an adult",
                              "Comfortable walking shoes recommended",
                              "Small-group tour for a personalized experience",
                              "Tour is weather-dependent",
                            ].map((info, i) => (
                              <div key={i} className="flex items-start">
                                <div className="h-5 w-5 rounded-full bg-indigo-100 flex items-center justify-center mr-2 mt-0.5">
                                  <div className="h-2 w-2 rounded-full bg-indigo-500"></div>
                                </div>
                                <p className="text-sm text-gray-200">{info}</p>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            id="acceptedTerms"
                            name="acceptedTerms"
                            checked={formData.acceptedTerms}
                            onChange={handleChange}
                            className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                          />
                          <label
                            htmlFor="acceptedTerms"
                            className="text-sm font-medium text-gray-200"
                          >
                            I accept that all the information provided is
                            correct and agree to the terms and conditions.
                          </label>
                        </div>
                        {errors.acceptedTerms && (
                          <p className="text-red-500 text-sm mt-1">
                            {errors.acceptedTerms}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </form>
            </div>

            {/* Navigation Footer */}
            <div className="p-4 md:p-6 border-t border-gray-100/30 flex flex-col sm:flex-row justify-between items-center gap-4 bg-gray-50/40 backdrop-blur-sm">
              <button
                onClick={() => {
                  if (activeStep > 1) {
                    prevStep();
                    document
                      .getElementById("tour-form")
                      .scrollIntoView({ behavior: "smooth" });
                  }
                }}
                className={`px-4 md:px-6 py-2 md:py-2.5 rounded-lg font-medium transition-all duration-300 w-full sm:w-auto ${
                  activeStep > 1
                    ? "text-gray-200 hover:bg-gray-800 cursor-pointer"
                    : "text-gray-400 cursor-default"
                }`}
                disabled={activeStep === 1}
              >
                {activeStep > 1 ? "Previous" : "Start"}
              </button>

              <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-8 w-full sm:w-auto">
                <div className="hidden sm:flex space-x-3">
                  {[1, 2, 3].map((step) => (
                    <div
                      key={step}
                      className={`w-3 h-3 rounded-full transition-all duration-300 ${
                        activeStep === step
                          ? "bg-black scale-125"
                          : activeStep > step
                          ? "bg-gray-600"
                          : "bg-gray-300"
                      }`}
                    ></div>
                  ))}
                </div>

                {activeStep < 3 ? (
                  <button
                    type="button"
                    onClick={() => {
                      nextStep();
                      document
                        .getElementById("tour-form")
                        .scrollIntoView({ behavior: "smooth" });
                    }}
                    className={`w-full sm:w-auto relative overflow-hidden px-6 md:px-8 py-2 md:py-2.5 rounded-lg font-medium transition-all duration-300 bg-gradient-to-r from-gray-700 to-black hover:from-black hover:to-gray-800 text-white`}
                  >
                    <span className="relative z-10">
                      {activeStep === 2 ? "Review Booking" : "Continue"}
                    </span>
                    <div className="absolute inset-0 -z-10 bg-gradient-to-r from-white/20 to-transparent opacity-0 hover:opacity-100 transition-opacity"></div>
                  </button>
                ) : (
                  <button
                    type="submit"
                    onClick={(e) => {
                      handleSubmit(e);
                      document
                        .getElementById("tour-form")
                        .scrollIntoView({ behavior: "smooth" });
                    }}
                    disabled={isSubmitting || !formData.acceptedTerms}
                    className="w-full sm:w-auto relative overflow-hidden px-6 md:px-8 py-2 md:py-2.5 rounded-lg font-medium transition-all duration-300 bg-gradient-to-r from-gray-700 to-black hover:from-black hover:to-gray-800 text-white disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {isSubmitting ? (
                      <>
                        <svg
                          className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Processing...
                      </>
                    ) : (
                      <>
                        <span className="relative z-10">Confirm Booking</span>
                        <div className="absolute inset-0 -z-10 bg-gradient-to-r from-white/20 to-transparent opacity-0 hover:opacity-100 transition-opacity"></div>
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
      {/* CSS Animations */}
      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.4s ease-out;
        }

        .animate-slideUp {
          animation: slideUp 0.5s cubic-bezier(0.16, 1, 0.3, 1);
        }
      `}</style>
    </>
  );
};

export default TourForm;
